<script setup lang="ts">
  import { computed, ref, toRef, watch } from 'vue';
  import { VueDraggable } from 'vue-draggable-plus';
  import { SelectInput } from '@/shared/ui';
  import { ARCHIVING_DRAGGABLE_OPTIONS } from '../../lib/archiving-draggable-options';
  import { ARCHIVING_MODEL_OPTIONS, getRouteLabel } from '../../lib/archiving-path';
  import { filterFieldsByName } from '../../lib/matrix-fields';
  import type { ArchivingModel, ArchivingRouteDto } from '../../model/archiving.types';
  import type { MatrixField } from '../../model/matrix-field.types';
  import { useArchivingRoutes } from '../../model/useArchivingRoutes';
  import ArchivingPathPreview from './ArchivingPathPreview.vue';
  import ArchivingRouteRow from './ArchivingRouteRow.vue';

  const MODEL_LABEL_ID = 'record-config-electronic-archiving-model-label';
  const FIELDS_ERROR_MESSAGE = 'Não foi possível carregar os campos do formulário';
  const DATE_PLACEHOLDER = 'Selecione um campo de data';

  const props = defineProps<{
    fields: MatrixField[];
    fieldsFailed: boolean;
    electronicArchivingModel: ArchivingModel | null;
    archivingRoutes: ArchivingRouteDto[];
  }>();

  const emit = defineEmits<{
    'update:electronicArchivingModel': [value: ArchivingModel | null];
    'update:archivingRoutes': [value: ArchivingRouteDto[]];
  }>();

  const searchTerms = ref<Record<number, string>>({});

  const {
    model,
    setModel,
    isGeneric,
    firstRoute,
    genericRoutes,
    canAddRoute,
    canRemoveRoute,
    availableFields,
    addRoute,
    removeRoute,
    setRouteField,
    tokenPath,
    examplePath,
    helperText,
    unknownFieldNames,
    payload,
    reset,
  } = useArchivingRoutes(toRef(props, 'fields'), {
    model: props.electronicArchivingModel,
    routes: props.archivingRoutes,
  });

  // Until the page has the form's fields (or when it failed to load them) every saved route
  // would look unknown, so the warning waits for a real field list.
  const visibleUnknownFieldNames = computed(() =>
    props.fieldsFailed || props.fields.length === 0 ? [] : unknownFieldNames.value
  );

  function serialize(model: ArchivingModel | null, routes: ArchivingRouteDto[]): string {
    return JSON.stringify({ model, routes });
  }

  watch(payload, (next) => {
    emit('update:electronicArchivingModel', next.model);
    emit('update:archivingRoutes', next.routes);
  });

  watch(
    () => [props.electronicArchivingModel, props.archivingRoutes] as const,
    ([nextModel, nextRoutes]) => {
      const current = payload.value;
      if (serialize(current.model, current.routes) !== serialize(nextModel, nextRoutes)) {
        searchTerms.value = {};
        reset({ model: nextModel, routes: nextRoutes });
      }
    }
  );

  function onSelectModel(value: string | number): void {
    const option = ARCHIVING_MODEL_OPTIONS.find((candidate) => candidate.id === value);
    if (option) setModel(option.id);
  }

  function setSearchTerm(index: number, term: string): void {
    searchTerms.value = { ...searchTerms.value, [index]: term };
  }

  function optionsFor(index: number): MatrixField[] {
    return filterFieldsByName(availableFields(index), searchTerms.value[index] ?? '');
  }
</script>

<template>
  <div class="flex flex-col gap-4" data-testid="record-config-electronic-archiving">
    <div class="flex flex-col gap-2">
      <span :id="MODEL_LABEL_ID" class="text-sm font-semibold text-gray-07"
        >Modelo de arquivamento:</span
      >
      <SelectInput
        id="record-config-electronic-archiving-model"
        :model-value="model ?? ''"
        :options="ARCHIVING_MODEL_OPTIONS"
        label="Selecione"
        :auto-select-first="false"
        :aria-labelledby="MODEL_LABEL_ID"
        container-class="w-72"
        @update:model-value="onSelectModel"
      />
    </div>

    <p v-if="fieldsFailed" class="text-sm text-gray-05" data-testid="archiving-fields-error">
      {{ FIELDS_ERROR_MESSAGE }}
    </p>

    <template v-if="model !== null">
      <div class="flex flex-col gap-1">
        <span class="text-sm font-semibold text-gray-07">Rotas de arquivamento:</span>
        <p class="text-sm text-gray-05" data-testid="archiving-helper">{{ helperText }}</p>
      </div>

      <div class="flex flex-col gap-3">
        <ArchivingRouteRow
          :label="getRouteLabel(0)"
          :model-value="firstRoute.fieldName"
          :options="optionsFor(0)"
          :placeholder="DATE_PLACEHOLDER"
          :can-add="isGeneric"
          :add-disabled="!canAddRoute"
          :can-remove="false"
          :show-handle="false"
          @update:model-value="setRouteField(0, $event)"
          @search="setSearchTerm(0, $event)"
          @add="addRoute"
        />

        <VueDraggable
          v-if="isGeneric"
          v-model="genericRoutes"
          v-bind="ARCHIVING_DRAGGABLE_OPTIONS"
          class="flex flex-col gap-3"
          data-testid="archiving-generic-routes"
        >
          <ArchivingRouteRow
            v-for="(route, index) in genericRoutes"
            :key="route.key"
            :label="getRouteLabel(index + 1)"
            :model-value="route.fieldName"
            :options="optionsFor(index + 1)"
            :can-add="false"
            :can-remove="canRemoveRoute(index + 1)"
            show-handle
            @update:model-value="setRouteField(index + 1, $event)"
            @search="setSearchTerm(index + 1, $event)"
            @remove="removeRoute(index + 1)"
          />
        </VueDraggable>
      </div>

      <p
        v-for="name in visibleUnknownFieldNames"
        :key="name"
        class="text-sm text-red-16"
        data-testid="archiving-unknown-field"
      >
        O campo {{ name }} não existe mais no formulário; selecione outro.
      </p>

      <ArchivingPathPreview :token-path="tokenPath" :example-path="examplePath" />
    </template>
  </div>
</template>
